import {
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionButton } from 'src/routes/components';
import { DisplayTextFields } from '../../perps-v1-shared/DisplayTextFields';
import { displayLabel } from '../../perps-v1-shared/api';
import { PERPS_V1_SYMBOLS_PERMISSION } from '../../perps-v1-shared/constants';
import { errorMessage } from '../../perps-v1-shared/errorMessage';
import { AdminMarket, DisplayText, MarketSector, MarketView } from '../../perps-v1-shared/types';
import { SymbolMembershipRow, SymbolRow, SymbolSectorTag, membershipApi, symbolsApi } from '../dataProvider';

// native symbols (dex === '') 只能挂 native|any 范围的板块；dex 币种（如 xyz）只能挂 hip3|any
function scopesForDex(dex: string | undefined): MarketSector['scope'][] {
  return dex ? ['hip3', 'any'] : ['native', 'any'];
}

interface DisplayFormState {
  logoUrl: string;
  name: DisplayText;
}

function blankDisplayForm(row: SymbolRow | null): DisplayFormState {
  return {
    logoUrl: row?.logoUrl ?? '',
    name: row?.name ?? {},
  };
}

interface Props {
  open: boolean;
  row: SymbolRow | null;
  sectors: MarketSector[];
  views: MarketView[];
  onClose: () => void;
  onSaved: (updatedRow?: Partial<SymbolRow>) => void;
}

// 从根到叶拼出祖先路径（含叶子自身），如 "TradFi › Stocks › US Stocks"；
// seen 防御 parentCode 成环导致死循环
function ancestorPath(view: MarketView, byCode: Map<string, MarketView>): string {
  const chain: string[] = [];
  const seen = new Set<string>();
  let current: MarketView | undefined = view;
  while (current && !seen.has(current.code)) {
    seen.add(current.code);
    chain.unshift(displayLabel(current.label, current.code));
    current = current.parentCode ? byCode.get(current.parentCode) : undefined;
  }
  return chain.join(' › ');
}

export default function SymbolEditorDialog({ open, row, sectors, views, onClose, onSaved }: Props) {
  const translate = useTranslate();
  const notify = useNotify();

  const [displayForm, setDisplayForm] = useState<DisplayFormState>(() => blankDisplayForm(row));
  const [savingDisplay, setSavingDisplay] = useState(false);
  const [deletingDisplay, setDeletingDisplay] = useState(false);

  const [selectedSectorCodes, setSelectedSectorCodes] = useState<string[]>([]);
  const [savingTags, setSavingTags] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const [membershipRows, setMembershipRows] = useState<SymbolMembershipRow[]>([]);
  const [checkedViewCodes, setCheckedViewCodes] = useState<string[]>([]);
  const [loadingMembership, setLoadingMembership] = useState(false);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [savingMembership, setSavingMembership] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDisplayForm(blankDisplayForm(row));
    setSelectedSectorCodes((row?.sectors ?? []).map((s) => s.code));
  }, [open, row]);

  // 依赖 symbol 而非整个 row 对象：display/tags 保存后 List 会 patch editingRow 生成新引用，
  // 若依赖 row 会导致这里重新拉取 membership，丢弃用户尚未保存的勾选编辑
  const symbol = row?.symbol;
  useEffect(() => {
    if (!open || !symbol) {
      setMembershipRows([]);
      setCheckedViewCodes([]);
      setMembershipError(null);
      return undefined;
    }
    let active = true;
    setLoadingMembership(true);
    setMembershipError(null);
    membershipApi
      .get(symbol)
      .then((json) => {
        if (!active) return;
        const rows = json?.data ?? [];
        setMembershipRows(rows);
        setCheckedViewCodes(
          Array.from(new Set(rows.filter((r) => r.source === 'manual').map((r) => r.viewCode)))
        );
      })
      .catch((error: any) => {
        if (!active) return;
        setMembershipRows([]);
        setCheckedViewCodes([]);
        setMembershipError(
          errorMessage(error, translate('module.perpsV1Symbols.membershipLoadError'))
        );
      })
      .finally(() => {
        if (active) setLoadingMembership(false);
      });
    return () => {
      active = false;
    };
  }, [open, symbol, translate]);

  const eligibleSectors = useMemo(() => {
    const scopes = scopesForDex(row?.dex);
    return sectors.filter((s) => scopes.includes(s.scope));
  }, [sectors, row?.dex]);

  const sectorLabel = useMemo(() => {
    const map = new Map(sectors.map((s) => [s.code, displayLabel(s.name, s.code)]));
    return (code: string) => map.get(code) ?? code;
  }, [sectors]);

  // 叶子节点 = 没有任何 view 把它当作 parentCode 的节点；用全量 views（含 disabled）判定树结构，
  // 再按 enabled 过滤出可勾选的行，避免被禁用的子节点误让父节点显示为可挂载的“叶子”
  const viewByCode = useMemo(() => new Map(views.map((v) => [v.code, v])), [views]);
  const leafViews = useMemo(() => {
    const parentCodes = new Set(views.map((v) => v.parentCode).filter((c): c is string => Boolean(c)));
    return views
      .filter((v) => v.enabled && !parentCodes.has(v.code))
      .map((v) => ({ view: v, path: ancestorPath(v, viewByCode) }))
      .sort((a, b) => a.path.localeCompare(b.path));
  }, [views, viewByCode]);

  const ruleViewCodes = useMemo(
    () => new Set(membershipRows.filter((r) => r.source === 'rule').map((r) => r.viewCode)),
    [membershipRows]
  );

  const setDisplay = (patch: Partial<DisplayFormState>) => setDisplayForm((prev) => ({ ...prev, ...patch }));

  const toggleMembership = (code: string) => {
    setCheckedViewCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const toggleSector = (code: string) => {
    setSelectedSectorCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const handleSaveDisplay = async () => {
    if (!row) return;
    setSavingDisplay(true);
    try {
      const logoUrl = displayForm.logoUrl.trim() || null;
      await symbolsApi.upsertDisplay({
        symbol: row.symbol,
        logoUrl,
        name: displayForm.name,
      });
      notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
      // PUT /display 是全量覆盖，响应体不含完整行；这里用提交的值直接 patch editingRow，
      // 避免弹窗仍打开时 hasDisplayMeta/logoUrl/name 与刚保存的值不一致。
      onSaved({ hasDisplayMeta: true, logoUrl, name: displayForm.name });
    } catch (error: any) {
      notify(errorMessage(error, translate('module.perpsV1Shared.saveError')), {
        type: 'error',
      });
    } finally {
      setSavingDisplay(false);
    }
  };

  const handleDeleteDisplay = async () => {
    if (!row) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(translate('module.perpsV1Shared.confirmDelete'))) return;
    setDeletingDisplay(true);
    try {
      await symbolsApi.deleteDisplay(row.symbol);
      setDisplayForm({ logoUrl: '', name: {} });
      notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
      onSaved({ hasDisplayMeta: false, logoUrl: null, name: null });
    } catch (error: any) {
      notify(errorMessage(error, translate('module.perpsV1Shared.saveError')), {
        type: 'error',
      });
    } finally {
      setDeletingDisplay(false);
    }
  };

  const handleSaveTags = async () => {
    if (!row) return;
    if (selectedSectorCodes.length === 0) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(translate('module.perpsV1Symbols.clearTagsConfirm'))) return;
    }
    setSavingTags(true);
    try {
      const payload: SymbolSectorTag[] = selectedSectorCodes.map((code) => ({ code }));
      const updated: AdminMarket = await symbolsApi.setTags(row.symbol, payload);
      notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
      onSaved(updated);
    } catch (error: any) {
      notify(errorMessage(error, translate('module.perpsV1Shared.saveError')), {
        type: 'error',
      });
    } finally {
      setSavingTags(false);
    }
  };

  const handleUnlock = async () => {
    if (!row) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(translate('module.perpsV1Symbols.unlockConfirm'))) return;
    setUnlocking(true);
    try {
      const updated: AdminMarket = await symbolsApi.unlockTags(row.symbol);
      // A clean reset can legitimately land on zero tags (nothing upstream, or the best-effort
      // re-sync failed) — that must not read as an error or a blank/broken dialog.
      if ((updated.sectors ?? []).length === 0) {
        notify(translate('module.perpsV1Symbols.unlockEmptyResult'), { type: 'info' });
      } else {
        notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
      }
      onSaved(updated);
    } catch (error: any) {
      notify(errorMessage(error, translate('module.perpsV1Shared.saveError')), {
        type: 'error',
      });
    } finally {
      setUnlocking(false);
    }
  };

  const handleSaveMembership = async () => {
    if (!row) return;
    setSavingMembership(true);
    try {
      // PUT 是 manual 挂载的全量覆盖；响应体是刷新后的完整 membership，直接回填替代重新拉取
      const fresh = await membershipApi.save(row.symbol, checkedViewCodes);
      const rows = fresh?.data ?? [];
      setMembershipRows(rows);
      setCheckedViewCodes(
        Array.from(new Set(rows.filter((r) => r.source === 'manual').map((r) => r.viewCode)))
      );
      notify(translate('module.perpsV1Symbols.membershipSaveSuccess'), { type: 'success' });
      onSaved();
    } catch (error: any) {
      notify(errorMessage(error, translate('module.perpsV1Shared.saveError')), {
        type: 'error',
      });
    } finally {
      setSavingMembership(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {translate('module.perpsV1Symbols.editSymbol')}
        {row ? ` — ${row.displaySymbol || row.symbol}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 0.5 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">{translate('module.perpsV1Symbols.displaySection')}</Typography>
            <TextField
              fullWidth
              size="small"
              label={translate('module.perpsV1Symbols.fields.logoUrl')}
              value={displayForm.logoUrl}
              onChange={(e) => setDisplay({ logoUrl: e.target.value })}
            />
            <DisplayTextFields
              value={displayForm.name}
              onChange={(v) => setDisplay({ name: v })}
              labelPrefix="module.perpsV1Shared.label"
              slugPlaceholder={`market.symbol.${row?.symbol ?? ''}`}
            />
            {row && !row.hasDisplayMeta ? (
              <Typography variant="caption" color="text.secondary">
                {translate('module.perpsV1Symbols.noDisplayMeta')}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <PermissionButton resource={PERPS_V1_SYMBOLS_PERMISSION} action="update">
                <LoadingButton
                  color="error"
                  onClick={handleDeleteDisplay}
                  loading={deletingDisplay}
                  disabled={!row?.hasDisplayMeta}
                >
                  {translate('module.perpsV1Shared.delete')}
                </LoadingButton>
              </PermissionButton>
              <PermissionButton resource={PERPS_V1_SYMBOLS_PERMISSION} action="update">
                <LoadingButton variant="contained" onClick={handleSaveDisplay} loading={savingDisplay}>
                  {translate('module.perpsV1Shared.save')}
                </LoadingButton>
              </PermissionButton>
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">{translate('module.perpsV1Symbols.tagsSection')}</Typography>
            {/* 只列出 scope 与该 symbol 所在 dex 相容的板块，所以这里的数量通常少于板块总数。
                不加说明的话，管理员会疑惑「一共 11 个板块，为什么只看到 6 个」。 */}
            {sectors.length > eligibleSectors.length ? (
              <Typography variant="caption" color="text.secondary">
                {translate('module.perpsV1Symbols.tagsScopeHint', {
                  shown: eligibleSectors.length,
                  total: sectors.length,
                  scope: row?.dex ? row.dex : translate('module.perpsV1Symbols.dexNative'),
                })}
              </Typography>
            ) : null}
            {eligibleSectors.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {translate('module.perpsV1Symbols.noSectorsForScope')}
              </Typography>
            ) : (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {eligibleSectors.map((s) => {
                  const selected = selectedSectorCodes.includes(s.code);
                  return (
                    <Chip
                      key={s.code}
                      clickable
                      label={sectorLabel(s.code)}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => toggleSector(s.code)}
                    />
                  );
                })}
              </Stack>
            )}
            {/* Save 无 disabled 条件：任意数量的标签（含 0 个）都是合法的一次保存，
                清空标签由 handleSaveTags 里的二次确认兜底 */}
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <PermissionButton resource={PERPS_V1_SYMBOLS_PERMISSION} action="update">
                <LoadingButton onClick={handleUnlock} loading={unlocking} disabled={!row?.tagsLocked}>
                  {translate('module.perpsV1Symbols.unlock')}
                </LoadingButton>
              </PermissionButton>
              <PermissionButton resource={PERPS_V1_SYMBOLS_PERMISSION} action="update">
                <LoadingButton variant="contained" onClick={handleSaveTags} loading={savingTags}>
                  {translate('module.perpsV1Shared.save')}
                </LoadingButton>
              </PermissionButton>
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">{translate('module.perpsV1Symbols.membershipSection')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {translate('module.perpsV1Symbols.membershipHint')}
            </Typography>
            {loadingMembership ? (
              <Typography variant="body2" color="text.secondary">
                {translate('common.loading')}
              </Typography>
            ) : membershipError ? (
              <Typography variant="body2" color="error">
                {membershipError}
              </Typography>
            ) : leafViews.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {translate('module.perpsV1Symbols.membershipNoViews')}
              </Typography>
            ) : (
              <FormGroup>
                {leafViews.map(({ view, path }) => {
                  const checked = checkedViewCodes.includes(view.code);
                  const isRule = ruleViewCodes.has(view.code);
                  // 仅靠规则命中（没有手动挂载）的行锁定：它本来就在该榜单里，勾选并不是
                  // 「确认」，而是加进 includeSymbols 把它顶到榜单最前面（排在置顶之后），
                  // 会在无意中改动线上排序。要提排名请到该榜单的「管理币种」里操作。
                  // 已手动挂载的行始终可点，否则就没法取消手动挂载了。
                  const ruleLocked = isRule && !checked;
                  return (
                    <Stack key={view.code} direction="row" spacing={1} alignItems="center">
                      <Tooltip
                        title={ruleLocked ? translate('module.perpsV1Symbols.membershipRuleLocked') : ''}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checked || ruleLocked}
                              disabled={ruleLocked}
                              onChange={() => toggleMembership(view.code)}
                            />
                          }
                          label={path}
                        />
                      </Tooltip>
                      {isRule ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="info"
                          label={translate('module.perpsV1Symbols.membershipRuleBadge')}
                        />
                      ) : null}
                    </Stack>
                  );
                })}
              </FormGroup>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <PermissionButton resource={PERPS_V1_SYMBOLS_PERMISSION} action="update">
                <LoadingButton
                  variant="contained"
                  onClick={handleSaveMembership}
                  loading={savingMembership}
                  // membershipError 时禁用：GET 失败后 checkedViewCodes 是空数组，
                  // 此时放行 Save 会全量清空该 symbol 的手动挂载（PUT 为整体替换）
                  disabled={loadingMembership || membershipError !== null}
                >
                  {translate('module.perpsV1Shared.save')}
                </LoadingButton>
              </PermissionButton>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose}>{translate('actions.close')}</LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
